"use client";

import { useState } from "react";
import type { SupplierEvaluationProject, SupplierCollaborator } from "../types";
import { addCollaborator, removeCollaborator } from "@/platform/firebase/firestore/supplier-evaluation";
import { sendCollaboratorInvite } from "../api";

interface Props {
  project: SupplierEvaluationProject;
  evaluationId: string;
  onSendToSupplier: () => void;
  onSendReminder: () => void;
  sendingInvite: boolean;
  sendingReminder: boolean;
  onSkip: () => void;
  skipping: boolean;
  onSkipRatings: () => void;
  skippingRatings: boolean;
  onProjectUpdate: (p: SupplierEvaluationProject) => void;
  onGoToScoring: () => void;
}

export function Step3SupplierResponse({
  project, evaluationId,
  onSendToSupplier, onSendReminder, sendingInvite, sendingReminder,
  onSkip, skipping, onSkipRatings, skippingRatings,
  onProjectUpdate, onGoToScoring,
}: Props) {
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [revokingEmail, setRevokingEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const portalLink = project.supplier_token
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/supplier/evaluation/${evaluationId}/${project.supplier_token}`
    : null;

  const handleInvite = async () => {
    const email = newEmail.trim();
    const name = newName.trim();
    if (!email.includes("@") || !name || !portalLink) return;
    if (project.shared_with?.some((c) => c.email === email)) return;
    setInviting(true);
    try {
      const token = crypto.randomUUID();
      const collaboratorLink = `${typeof window !== "undefined" ? window.location.origin : ""}/supplier/evaluation/${evaluationId}/${token}`;
      const collaborator: SupplierCollaborator = { name, email, token, invited_at: new Date().toISOString() };
      await sendCollaboratorInvite({ collaborator_email: email, collaborator_name: name, company_name: project.supplier_info.company_name, project_name: project.project_name, portal_link: collaboratorLink });
      await addCollaborator(evaluationId, collaborator);
      onProjectUpdate({ ...project, shared_with: [...(project.shared_with ?? []), collaborator] });
      setNewName(""); setNewEmail("");
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (c: SupplierCollaborator) => {
    setRevokingEmail(c.email);
    try {
      await removeCollaborator(evaluationId, c);
      onProjectUpdate({ ...project, shared_with: (project.shared_with ?? []).filter((x) => x.email !== c.email) });
    } finally {
      setRevokingEmail(null);
    }
  };

  const copyLink = () => {
    if (portalLink) { navigator.clipboard.writeText(portalLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Status card */}
      <div className="diag-card" style={{ gap: "14px" }}>
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600 }}>Supplier Response</h3>
        <p style={{ fontSize: "13px", color: "var(--ink-mute)", marginTop: "-8px" }}>{project.supplier_info.email}</p>

        {[
          { label: "Invitation Sent",    date: project.invitation_sent_at,   done: !!project.invitation_sent_at },
          { label: "Response Received",  date: project.supplier_completed_at, done: !!project.supplier_completed_at },
        ].map((item) => (
          <div key={item.label} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "18px" }}>{item.done ? "✅" : "⏳"}</span>
            <div>
              <p style={{ fontSize: "13px", fontWeight: 600, margin: 0 }}>{item.label}</p>
              <p style={{ fontSize: "12px", color: "var(--ink-mute)", margin: 0 }}>
                {item.date ? new Date(item.date).toLocaleString() : "Pending"}
              </p>
            </div>
          </div>
        ))}

        {project.supplier_completed_at && (
          <div style={{ paddingTop: "12px", borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: "8px" }}>
            <button onClick={onGoToScoring} style={primaryBtnStyle}>
              {project.auditor_scored_at ? "View Ratings" : "Proceed to My Ratings →"}
            </button>
            {!project.auditor_scored_at && (
              <button onClick={onSkipRatings} disabled={skippingRatings} style={ghostBtnStyle}>
                {skippingRatings ? "…" : "Skip My Ratings"}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Portal link + collaborators */}
      {project.supplier_token && (
        <div className="diag-card" style={{ gap: "14px" }}>
          <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>Supplier Portal Link</h3>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <code style={{ flex: 1, fontSize: "11px", background: "var(--paper-3)", borderRadius: "6px", padding: "8px 10px", color: "var(--ink-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", minWidth: 0 }}>
              {portalLink}
            </code>
            <button onClick={copyLink} style={{ ...ghostBtnStyle, width: "auto", flexShrink: 0 }}>{copied ? "✓" : "Copy"}</button>
          </div>

          {/* Respondents */}
          <div>
            <p style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-mute)", marginBottom: "8px" }}>
              Respondents
            </p>
            {/* Primary */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderRadius: "8px", background: "var(--paper-3)", marginBottom: "4px" }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: "13px", fontWeight: 600, margin: 0 }}>{project.supplier_info.contact_person}</p>
                <p style={{ fontSize: "12px", color: "var(--ink-mute)", margin: 0 }}>{project.supplier_info.email}</p>
              </div>
              <span style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "100px", background: "var(--paper-3)", color: "var(--ink-mute)", border: "1px solid var(--line)" }}>Primary</span>
            </div>
            {/* Collaborators */}
            {(project.shared_with ?? []).map((c) => (
              <div key={c.email} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px", borderRadius: "8px" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: "13px", fontWeight: 500, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</p>
                  <p style={{ fontSize: "12px", color: "var(--ink-mute)", margin: 0 }}>{c.email}</p>
                </div>
                <button onClick={() => handleRevoke(c)} disabled={revokingEmail === c.email} style={{ background: "none", border: "none", color: "var(--ink-mute)", cursor: "pointer", fontSize: "16px" }}>
                  {revokingEmail === c.email ? "…" : "×"}
                </button>
              </div>
            ))}
            {/* Invite row */}
            <div style={{ display: "flex", gap: "8px", paddingTop: "10px", borderTop: "1px solid var(--line)", marginTop: "8px" }}>
              <input placeholder="Name" value={newName} onChange={(e) => setNewName(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <input placeholder="Email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleInvite()} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={handleInvite} disabled={inviting || !newName.trim() || !newEmail.trim()} style={primaryBtnStyle}>
                {inviting ? "…" : "Invite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send / reminder */}
      {!project.supplier_completed_at && (
        <div className="diag-card" style={{ gap: "8px" }}>
          <button onClick={onSendToSupplier} disabled={sendingInvite || skipping} style={primaryBtnStyle}>
            {sendingInvite ? "Sending…" : project.invitation_sent_at ? "Resend Invitation" : "Send to Supplier"}
          </button>
          {project.invitation_sent_at && (
            <button onClick={onSendReminder} disabled={sendingReminder} style={ghostBtnStyle}>
              {sendingReminder ? "Sending…" : "Send Reminder"}
            </button>
          )}
          <button onClick={onSkip} disabled={skipping} style={ghostBtnStyle}>
            {skipping ? "…" : "Skip Supplier Response"}
          </button>
        </div>
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, var(--indigo), var(--magenta))",
  border: "none", borderRadius: "8px", padding: "10px 16px",
  fontSize: "13px", fontWeight: 600, color: "#fff", cursor: "pointer", width: "100%",
};
const ghostBtnStyle: React.CSSProperties = {
  background: "var(--paper-2)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "8px 14px", fontSize: "13px",
  color: "var(--ink-soft)", cursor: "pointer", width: "100%",
};
const inputStyle: React.CSSProperties = {
  background: "var(--paper-3)", border: "1px solid var(--line-strong)",
  borderRadius: "8px", padding: "7px 10px", fontSize: "13px", color: "var(--ink)",
};
